import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import { useEffect } from "react";

function getArticleContent(slug: string, t: (key: string) => string) {
  // Build section titles dynamically for any slug
  const sectionTitle = (key: string) => {
    const titles: Record<string, string> = {
      "allchinabuy-spreadsheet": t("article1.h1"),
      "allchinabuy-review": "AllChinaBuy Review 2026: Features, Pros, Cons & Buying Guide",
      "allchinabuy-shipping": "AllChinaBuy Shipping Cost 2026: Complete Guide & Comparison",
      "allchinabuy-legit": "Is AllChinaBuy Legit? 2026 Safety & Trust Guide",
      "allchinabuy-coupon": "AllChinaBuy Coupon 2026: How Discounts Work",
      "allchinabuy-warehouse": "AllChinaBuy Warehouse Guide: Storage, QC & Shipping",
      "allchinabuy-buying-guide": "AllChinaBuy Buying Guide 2026: How to Shop Smart",
      "allchinabuy-qc-guide": "AllChinaBuy QC Guide: How to Read QC Photos",
      "allchinabuy-shipping-cost": "AllChinaBuy Shipping Cost 2026: Complete Guide",
      "allchinabuy-discount-code": "AllChinaBuy Discount Code 2026: Offers & Savings",
      "allchinabuy-seller-guide": "AllChinaBuy Seller Guide: How to Evaluate Sellers",
      "allchinabuy-best-finds": "Best AllChinaBuy Finds 2026: Popular Products",
      "allchinabuy-safety-guide": "AllChinaBuy Safety Guide: Buyer Protection Tips",
      "allchinabuy-beginner-guide": "AllChinaBuy Beginner Guide: Getting Started",
      "allchinabuy-product-research": "AllChinaBuy Product Research Guide: Find Deals",
      "allchinabuy-parcel-consolidation": "AllChinaBuy Parcel Consolidation: Save on Shipping",
    };
    return titles[slug] || t("article1.h1");
  };

  const introText = (key: string) => {
    const intros: Record<string, string> = {
      "allchinabuy-spreadsheet": t("article1.intro"),
      "allchinabuy-review": "AllChinaBuy is a popular platform connecting international buyers with products from China. In this review, we examine its features, warehouse services, shipping options, and honest pros and cons for buyers. This guide helps you decide whether AllChinaBuy is the right platform for your needs.",
      "allchinabuy-shipping": "Shipping costs can make or break the value of an international purchase. This guide covers everything you need to know about AllChinaBuy shipping costs, delivery times, shipping lines, and how to calculate volumetric weight for international orders. Understanding these factors helps you plan better and save money.",
      "allchinabuy-legit": "When buying from international platforms, safety and trust are top concerns. This guide covers seller trust, payment security, counterfeit risks, privacy considerations, and red flags to watch for when buying through AllChinaBuy. We provide an honest assessment to help you make informed decisions.",
      "allchinabuy-coupon": "Coupons and discount codes can significantly reduce your total order cost. This guide explains how AllChinaBuy coupons work, different types of discounts available, eligibility requirements, and how to maximize savings on your orders. Always verify current offers before placing your order.",
      "allchinabuy-warehouse": "The warehouse process is a critical part of the AllChinaBuy experience. This guide covers how warehouse storage works, QC inspection procedures, package consolidation options, and shipping preparation. Understanding these processes helps you manage orders more effectively and avoid delays.",
      "allchinabuy-buying-guide": "If you are new to AllChinaBuy, this step-by-step guide covers everything from product selection to order placement. Learn how to evaluate sellers, compare prices, understand QC photos, and choose the best shipping options. Follow these strategies to avoid common purchasing mistakes and have a smoother buying experience.",
      "allchinabuy-qc-guide": "Quality Control photos are essential for verifying product quality before shipment. This guide teaches you how to read QC photos effectively, spot common defects, and decide whether to approve or reject items. Learn what details to look for and how to communicate quality concerns with the platform.",
      "allchinabuy-shipping-cost": "Understanding the factors that determine shipping costs helps you budget accurately. This guide covers weight calculation, destination pricing, service types, volumetric weight formulas, and optional insurance. We break down each factor so you can estimate your total shipping cost before committing to an order.",
      "allchinabuy-discount-code": "Discount codes and promo offers can provide meaningful savings on your AllChinaBuy orders. This guide explains how discount codes work, where to find current offers, eligibility rules, and practical tips to apply savings. We also clarify common misconceptions about AllChinaBuy promotions and deals.",
      "allchinabuy-seller-guide": "Not all sellers are equally reliable. This practical guide teaches you how to evaluate seller trustworthiness, check seller history and reviews, understand ratings, and identify red flags. Reducing seller risk starts with informed research before you commit to any purchase.",
      "allchinabuy-best-finds": "This curated guide highlights the most popular AllChinaBuy finds across all categories. We explore what makes certain products worth buying, how to identify quality items, and which categories consistently deliver the best value. Use these insights to make smarter purchasing decisions in 2026.",
      "allchinabuy-safety-guide": "Protecting yourself as an international buyer requires awareness and caution. This guide covers essential safety tips including payment protection methods, privacy best practices, scam prevention techniques, and a practical buyer checklist. Following these guidelines reduces risk significantly.",
      "allchinabuy-beginner-guide": "Starting with AllChinaBuy can feel overwhelming. This beginner-friendly guide walks you through account creation, understanding the interface, placing your first order, and navigating the post-purchase process. Learn the basics quickly so you can shop with confidence.",
      "allchinabuy-product-research": "Effective product research separates great purchases from disappointing ones. This guide teaches you how to compare prices across sellers, verify product authenticity, read reviews critically, and identify genuine quality before committing. Better research leads to better buying outcomes.",
      "allchinabuy-parcel-consolidation": "Parcel consolidation can dramatically reduce your per-item shipping cost. This guide explains how combining multiple packages into one shipment works, when consolidation is beneficial, and how to optimize your package bundling. Learn the best practices to maximize savings on international shipping.",
    };
    return intros[slug] || t("article1.intro");
  };

  const baseSections = [
    { title: t("article1.whatis.title"), text: t("article1.whatis.text") },
    { title: t("article1.howto.title"), text: t("article1.howto.text") },
    { title: t("article1.categories.title"), text: t("article1.categories.text") },
    { title: t("article1.qc.title"), text: t("article1.qc.text") },
    { title: t("article1.shipping.title"), text: t("article1.shipping.text") },
    { title: t("article1.safety.title"), text: t("article1.safety.text") },
  ];

  return {
    h1: sectionTitle(slug),
    intro: introText(slug),
    sections: baseSections,
    faqs: [
      { q: t("article1.faq1.q"), a: t("article1.faq1.a") },
      { q: t("article1.faq2.q"), a: t("article1.faq2.a") },
      { q: t("article1.faq3.q"), a: t("article1.faq3.a") },
      { q: t("article1.faq4.q"), a: t("article1.faq4.a") },
    ],
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const activeSlug = slug || "allchinabuy-spreadsheet";
  const article = getArticleContent(activeSlug, t);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Article Hero */}
      <div className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 text-sm text-primary-500 font-medium mb-4 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-600">
              Spreadsheet
            </span>
            <span className="text-foreground-400">|</span>
            <span className="text-foreground-500">{t("article1.date")}</span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
            {article.h1}
          </h1>

          <p className="text-lg md:text-xl text-foreground-600 leading-relaxed mb-8">
            {article.intro}
          </p>

          <div className="flex flex-wrap gap-3 mb-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-2" />
              {t("hero.cta1")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-secondary whitespace-nowrap"
            >
              {t("hero.cta2")}
            </a>
          </div>

          <div className="flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              12 min read
            </span>
            <span>|</span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {t("article1.date")}
            </span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="bg-white rounded-lg border border-background-200/70 p-6 md:p-10">
          {article.sections.map((section, idx) => (
            <div key={idx} className="mb-10 last:mb-0">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {section.title}
              </h2>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                {section.text}
              </p>
            </div>
          ))}

          {/* FAQ */}
          <div className="mt-12 pt-10 border-t border-background-200/70">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              {t("article1.faq.title")}
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-background-200/70 bg-background-50/50 p-5"
                >
                  <h3 className="font-semibold text-foreground-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-primary-500 mt-0.5" />
                    {faq.q}
                  </h3>
                  <p className="text-foreground-600 leading-relaxed pl-7">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-6 md:p-8 rounded-lg bg-primary-50/50 border border-primary-200/50 text-center">
            <h3 className="font-display text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              {t("finalcta.h2")}
            </h3>
            <p className="text-foreground-600 mb-6 max-w-2xl mx-auto">
              {t("finalcta.text")}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary inline-flex"
            >
              <i className="ri-external-link-line mr-2" />
              {t("article1.cta")}
            </a>
          </div>

          {/* Internal Link to Home */}
          <div className="mt-10 flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-primary-500 font-medium hover:text-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t("article1.backToHome")}
            </button>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-10 border-t border-background-200/70">
            <h3 className="font-display text-xl font-bold text-foreground-950 mb-6">
              {t("article1.relatedArticles")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: "article1.related1", path: "/allchinabuy-review" },
                { key: "article1.related2", path: "/allchinabuy-shipping" },
                { key: "article1.related3", path: "/allchinabuy-legit" },
              ].map((rel, idx) => (
                <button
                  key={idx}
                  onClick={() => navigate(rel.path)}
                  className="text-left p-4 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all"
                >
                  <i className="ri-article-line text-primary-400 mb-2 block" />
                  <span className="text-sm font-medium text-foreground-800 hover:text-primary-500">
                    {t(rel.key)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-foreground-950 text-white/80 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h3 className="text-white font-semibold mb-3">AllChinaBuy</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                {t("footer.disclaimer")}
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("nav.spreadsheet")}</h4>
              <div className="space-y-2">
                <button onClick={() => navigate("/")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">{t("nav.home")}</button>
                <button onClick={() => navigate("/allchinabuy-spreadsheet")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Spreadsheet</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("nav.guide")}</h4>
              <div className="space-y-2">
                <button onClick={() => navigate("/allchinabuy-buying-guide")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Buying Guide</button>
                <button onClick={() => navigate("/allchinabuy-safety-guide")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Safety Guide</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("footer.contact")}</h4>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">kaspreadsheet.com</a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-sm text-white/50">
            {t("footer.copyright")}
          </div>
        </div>
      </footer>
    </div>
  );
}